import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import Input from "../../../components/Input/Input";
import Select from "../../../components/Select/Select";
import "./PaymentMethod.css";
//services
import { paymentService } from "../../../services/Payment";

// Inicializar Stripe con la clave de la variable de entorno
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const PaymentMethodForm = () => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    holderName: "",
    country: "",
    postalCode: "",
    saveCard: false,
  });
  const [errors, setErrors] = useState({
    holderName: "",
    country: "",
    postalCode: "",
    card: "",
  });
  const [token, setToken] = useState(null);
  const [language, setLanguage] = useState(null);
  const [customerId, setCustomerId] = useState(null);

  const { token: tokenFromParams, language: languageFromParams } = useParams();

  useEffect(() => {
    if (tokenFromParams && languageFromParams) {
      setToken(tokenFromParams);
      setLanguage(languageFromParams);
      fetchUser(tokenFromParams);
    }
  }, [tokenFromParams, languageFromParams]);

  // Effect para cambiar el idioma cuando cambie la variable language
  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language.toLowerCase());
    }
  }, [language]);

  const fetchUser = async (currentToken) => {
    try {
      const response = await paymentService.getDataMe(currentToken || token);
      console.log("User data:", response);
      if (response.status === "success") {
        console.log("User data:", response);
        fetchCustomerId(response.data.id, currentToken || token);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      setError(t("common.error.userDataFetch"));
    }
  };

  const fetchCustomerId = async (userId, currentToken) => {
    try {
      const body = {
        user_id: userId,
      };
      const response = await paymentService.getCustomerId(
        body,
        currentToken || token,
      );
      console.log("Customer ID:", response);
      if (response.status === "success") {
        setCustomerId(response.data.customer_id);
      }
    } catch (error) {
      console.error("Error fetching customer ID:", error);
      setError(t("common.error.customerIdFetch"));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Limpiar error cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let hasErrors = false;

    // Validación de campos requeridos
    if (!formData.holderName.trim()) {
      newErrors.holderName = t("payments.method.errors.holderNameRequired");
      hasErrors = true;
    } else if (
      formData.holderName.trim().length < 2 ||
      formData.holderName.trim().length > 250
    ) {
      newErrors.holderName = t("payments.method.errors.holderNameLength");
      hasErrors = true;
    }

    if (!formData.country) {
      newErrors.country = t("payments.method.errors.countryRequired");
      hasErrors = true;
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = t("payments.method.errors.postalCodeRequired");
      hasErrors = true;
    } else if (!/^\d{4,5}$/.test(formData.postalCode.trim())) {
      newErrors.postalCode = t("payments.method.errors.postalCodeLength");
      hasErrors = true;
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!stripe || !elements || !token) {
      const errorMessage = "No se puede procesar el pago en este momento";
      setError(errorMessage);

      // Enviar mensaje de error a la app nativa
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "PAYMENT_METHOD_INIT_ERROR",
            success: false,
            error: errorMessage,
            details: {
              hasStripe: !!stripe,
              hasElements: !!elements,
              hasToken: !!token,
            },
          }),
        );
      } else if (window.webkit?.messageHandlers?.paymentCallback) {
        window.webkit.messageHandlers.paymentCallback.postMessage({
          type: "PAYMENT_METHOD_INIT_ERROR",
          success: false,
          error: errorMessage,
          details: {
            hasStripe: !!stripe,
            hasElements: !!elements,
            hasToken: !!token,
          },
        });
      } else if (window.Android) {
        window.Android.onPaymentMethodError(
          JSON.stringify({
            type: "PAYMENT_METHOD_INIT_ERROR",
            success: false,
            error: errorMessage,
            details: {
              hasStripe: !!stripe,
              hasElements: !!elements,
              hasToken: !!token,
            },
          }),
        );
      }
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: cardError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement),
          billing_details: {
            name: formData.holderName,
            address: {
              country: "MX",
              postal_code: formData.postalCode,
            },
          },
        });

      if (cardError) {
        setError(cardError.message);
        return;
      }

      console.log("Payment Method:", paymentMethod);
      const response = await paymentService.assignPaymentMethod(
        paymentMethod.id,
        customerId,
      );
      console.log("Response:", response);

      if (response && response.id) {
        // Limpiar el formulario
        setFormData({
          holderName: "",
          country: "",
          postalCode: "",
          saveCard: true,
        });
        elements.getElement(CardElement).clear();

        // Enviar mensaje de éxito a la app nativa
        if (window.ReactNativeWebView) {
          // Para React Native WebView
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: "PAYMENT_METHOD_CREATED",
              success: true,
              data: {
                paymentMethodId: response.id,
                customerId: customerId,
                last4: response.card?.last4,
                brand: response.card?.brand,
              },
            }),
          );
        } else if (window.webkit?.messageHandlers?.paymentCallback) {
          // Para iOS WKWebView
          window.webkit.messageHandlers.paymentCallback.postMessage({
            type: "PAYMENT_METHOD_CREATED",
            success: true,
            data: {
              paymentMethodId: response.id,
              customerId: customerId,
              last4: response.card?.last4,
              brand: response.card?.brand,
            },
          });
        } else if (window.Android) {
          // Para Android WebView
          window.Android.onPaymentMethodCreated(
            JSON.stringify({
              type: "PAYMENT_METHOD_CREATED",
              success: true,
              data: {
                paymentMethodId: response.id,
                customerId: customerId,
                last4: response.card?.last4,
                brand: response.card?.brand,
              },
            }),
          );
        }
      }
    } catch (err) {
      const errorMessage =
        "Error al procesar la tarjeta. Por favor, intente nuevamente.";
      setError(errorMessage);

      // Enviar mensaje de error a la app nativa
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "PAYMENT_METHOD_ERROR",
            success: false,
            error: errorMessage,
          }),
        );
      } else if (window.webkit?.messageHandlers?.paymentCallback) {
        window.webkit.messageHandlers.paymentCallback.postMessage({
          type: "PAYMENT_METHOD_ERROR",
          success: false,
          error: errorMessage,
        });
      } else if (window.Android) {
        window.Android.onPaymentMethodError(
          JSON.stringify({
            type: "PAYMENT_METHOD_ERROR",
            success: false,
            error: errorMessage,
          }),
        );
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="payment-method-container">
      <form onSubmit={handleSubmit}>
        <div className="card-info-section">
          <div className="section-header">
            <h2 className="section-title">{t("payments.method.cardInfo")}</h2>
          </div>

          <Input
            type="text"
            size="half-width"
            value={formData.holderName}
            onChange={(e) =>
              handleInputChange({
                target: { name: "holderName", value: e.target.value },
              })
            }
            placeholder={t("payments.method.holderName")}
            error={errors.holderName}
          />

          <CardElement
            className={`card-element-container ${errors.card ? "error" : ""}`}
            options={{
              hidePostalCode: true,
              disableLink: true,
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424242",
                  fontFamily: "Arial, sans-serif",
                },
              },
            }}
            onChange={(e) => {
              console.log("Card element changed:", e);
              setErrors({
                card: e.error ? e.error.message : null,
              });
            }}
          />
          {errors.card && <div className="error-message">{errors.card}</div>}
        </div>

        <div className="billing-section">
          <h2 className="section-title">
            {t("payments.method.billingStates")}
          </h2>
          <Select
            options={[
              { value: "MX", label: t("payments.method.countries.mx") },
              { value: "US", label: t("payments.method.countries.us") },
            ]}
            value={formData.country}
            onChange={(e) =>
              handleInputChange({
                target: { name: "country", value: e.target.value },
              })
            }
            placeholder={t("payments.method.country")}
            required
            size="half-width"
            error={errors.country}
          />
          <Input
            type="text"
            size="half-width"
            value={formData.postalCode}
            onChange={(e) =>
              handleInputChange({
                target: { name: "postalCode", value: e.target.value },
              })
            }
            placeholder={t("payments.method.postalCode")}
            error={errors.postalCode}
          />
        </div>

        <div className="save-card-option">
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="saveCard"
              checked={formData.saveCard}
              onChange={handleInputChange}
            />
            {t("payments.method.saveCard")}
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          type="submit"
          className="save-button"
          disabled={!stripe || processing}
        >
          {processing
            ? t("payments.method.loading")
            : t("payments.method.saveButton")}
        </button>
      </form>
    </div>
  );
};

// Componente envuelto con el provider de Stripe
const PaymentMethod = (props) => (
  <Elements stripe={stripePromise}>
    <PaymentMethodForm {...props} />
  </Elements>
);

export default PaymentMethod;
